import { useState, useRef, useEffect } from "react"
import { useNotificationConfigs, useUpdateNotificationConfig, useResetNotificationConfig } from "@/hooks/useNotificationConfig"
import type { NotificationConfigItem, VariableInfo } from "@/hooks/useNotificationConfig"
import { Card } from "@/components/ui/Card"
import HtmlEditor from "@/components/ui/HtmlEditor"
import { Button } from "@/components/ui/Button"
import { Save, RotateCcw, ChevronDown, ChevronUp, Mail, Bell, Smartphone, Clock, Info, Copy, Check, Search, X } from "lucide-react"

const CATEGORY_LABELS: Record<string, string> = {
  email: "Email",
  crm: "CRM / Pipeline",
  system: "Sistema",
}
const CATEGORY_ORDER = ["email", "crm", "system"]

export default function NotificationSettings() {
  const { data, isLoading } = useNotificationConfigs()
  const updateMut = useUpdateNotificationConfig()
  const resetMut = useResetNotificationConfig()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Partial<NotificationConfigItem>>>({})
  const [saved, setSaved] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [varSearch, setVarSearch] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  if (isLoading) return <div className="flex items-center justify-center py-16 text-zinc-400">Carregando configurações...</div>
  if (!data) return null

  const { notifications, variables, recurrence_options } = data

  const grouped: Record<string, NotificationConfigItem[]> = {}
  for (const n of notifications) {
    if (!grouped[n.category]) grouped[n.category] = []
    grouped[n.category].push(n)
  }

  function getDraft(type: string): Partial<NotificationConfigItem> {
    return drafts[type] || {}
  }

  function getVal<K extends keyof NotificationConfigItem>(n: NotificationConfigItem, key: K) {
    const d = getDraft(n.notification_type)
    return (key in d ? d[key] : n[key]) as NotificationConfigItem[K]
  }

  function setField(type: string, key: string, value: any) {
    setDrafts(prev => ({ ...prev, [type]: { ...prev[type], [key]: value } }))
  }

  async function handleSave(type: string) {
    const d = drafts[type]
    if (!d || Object.keys(d).length === 0) return
    await updateMut.mutateAsync({ type, payload: d })
    setDrafts(prev => { const c = { ...prev }; delete c[type]; return c })
    setSaved(type)
    setTimeout(() => setSaved(null), 2000)
  }

  async function handleReset(type: string) {
    if (!confirm("Restaurar esta notificação para o padrão original?")) return
    await resetMut.mutateAsync(type)
    setDrafts(prev => { const c = { ...prev }; delete c[type]; return c })
  }

  function insertVariable(varKey: string) {
    if (textareaRef.current) {
      const ta = textareaRef.current
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const val = ta.value
      const newVal = val.substring(0, start) + varKey + val.substring(end)
      ta.value = newVal
      ta.selectionStart = ta.selectionEnd = start + varKey.length
      ta.focus()
      // trigger change
      const type = expanded!
      setField(type, "email_body_html", newVal)
    }
    navigator.clipboard.writeText(varKey)
    setCopied(varKey)
    setTimeout(() => setCopied(null), 1500)
  }

  const filteredVars = variables.filter(v =>
    !varSearch || v.key.toLowerCase().includes(varSearch.toLowerCase()) || v.description.toLowerCase().includes(varSearch.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Configuração de Notificações</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Gerencie os canais, recorrência e templates de cada notificação da plataforma.
          </p>
        </div>
        <span className="text-xs text-zinc-400 dark:text-zinc-500">{notifications.length} notificações</span>
      </div>

      {/* Notification Cards by Category */}
      {CATEGORY_ORDER.map(cat => {
        const items = grouped[cat]
        if (!items?.length) return null
        return (
          <div key={cat}>
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-3 flex items-center gap-2">
              {cat === "email" && <Mail size={14} />}
              {cat === "crm" && <Bell size={14} />}
              {cat === "system" && <Info size={14} />}
              {CATEGORY_LABELS[cat] || cat}
            </h4>
            <div className="space-y-2">
              {items.map(n => {
                const isExpanded = expanded === n.notification_type
                const enabled = getVal(n, "is_enabled")
                const hasDraft = drafts[n.notification_type] && Object.keys(drafts[n.notification_type]).length > 0

                return (
                  <Card key={n.notification_type} className={`overflow-hidden transition-all ${isExpanded ? "ring-2 ring-blue-500/30 dark:ring-blue-400/20" : ""}`}>
                    {/* Row header */}
                    <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50" onClick={() => setExpanded(isExpanded ? null : n.notification_type)}>
                      {/* Toggle */}
                      <button
                        onClick={e => { e.stopPropagation(); setField(n.notification_type, "is_enabled", !enabled) }}
                        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${enabled ? "bg-blue-500" : "bg-zinc-300 dark:bg-zinc-600"}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? "left-5.5 translate-x-0" : "left-0.5"}`}
                          style={{ left: enabled ? "22px" : "2px" }}
                        />
                      </button>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${enabled ? "text-zinc-900 dark:text-white" : "text-zinc-400 dark:text-zinc-500"}`}>{n.label}</span>
                          {hasDraft && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="Alterações não salvas" />}
                          {saved === n.notification_type && <span className="text-xs text-green-500 font-medium">Salvo!</span>}
                        </div>
                        <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate">{n.description}</p>
                      </div>

                      {/* Channel badges */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {n.available_channels.includes("email") && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getVal(n, "email_enabled") ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" : "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-600"}`}>
                            Email
                          </span>
                        )}
                        {n.available_channels.includes("push") && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getVal(n, "push_enabled") ? "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" : "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-600"}`}>
                            Push
                          </span>
                        )}
                        {n.available_channels.includes("in_app") && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getVal(n, "in_app_enabled") ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-600"}`}>
                            In-App
                          </span>
                        )}
                      </div>

                      {/* Expand arrow */}
                      {isExpanded ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                    </div>

                    {/* Expanded panel */}
                    {isExpanded && (
                      <div className="border-t border-zinc-100 dark:border-zinc-800 px-4 py-4 space-y-4 bg-zinc-50/50 dark:bg-zinc-900/30">
                        {/* Channel toggles */}
                        <div>
                          <label className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2 block">Canais</label>
                          <div className="flex flex-wrap gap-3">
                            {n.available_channels.includes("email") && (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={!!getVal(n, "email_enabled")} onChange={e => setField(n.notification_type, "email_enabled", e.target.checked)}
                                  className="w-4 h-4 rounded border-zinc-300 text-blue-500 focus:ring-blue-500" />
                                <Mail size={14} className="text-blue-500" />
                                <span className="text-sm text-zinc-700 dark:text-zinc-300">Email</span>
                              </label>
                            )}
                            {n.available_channels.includes("push") && (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={!!getVal(n, "push_enabled")} onChange={e => setField(n.notification_type, "push_enabled", e.target.checked)}
                                  className="w-4 h-4 rounded border-zinc-300 text-purple-500 focus:ring-purple-500" />
                                <Smartphone size={14} className="text-purple-500" />
                                <span className="text-sm text-zinc-700 dark:text-zinc-300">Push</span>
                              </label>
                            )}
                            {n.available_channels.includes("in_app") && (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={!!getVal(n, "in_app_enabled")} onChange={e => setField(n.notification_type, "in_app_enabled", e.target.checked)}
                                  className="w-4 h-4 rounded border-zinc-300 text-green-500 focus:ring-green-500" />
                                <Bell size={14} className="text-green-500" />
                                <span className="text-sm text-zinc-700 dark:text-zinc-300">In-App</span>
                              </label>
                            )}
                          </div>
                        </div>

                        {/* Recurrence */}
                        <div>
                          <label className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <Clock size={12} /> Recorrência
                          </label>
                          <select
                            value={getVal(n, "recurrence")}
                            onChange={e => setField(n.notification_type, "recurrence", e.target.value)}
                            className="w-full max-w-xs px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            {recurrence_options.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {getVal(n, "recurrence") === "custom_cron" && (
                            <input
                              type="text" placeholder="Ex: 0 9 * * 1 (seg 9h)"
                              value={getVal(n, "cron_expression") || ""}
                              onChange={e => setField(n.notification_type, "cron_expression", e.target.value)}
                              className="mt-2 w-full max-w-xs px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white font-mono"
                            />
                          )}
                        </div>

                        {/* Email template */}
                        {n.available_channels.includes("email") && (
                          <div>
                            <label className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <Mail size={12} /> Template do Email
                            </label>
                            <input
                              type="text" placeholder="Assunto do email"
                              value={getVal(n, "email_subject") || ""}
                              onChange={e => setField(n.notification_type, "email_subject", e.target.value)}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white mb-2"
                            />
                            <HtmlEditor
                              value={getVal(n, "email_body_html") || ""}
                              onChange={(html) => setField(n.notification_type, "email_body_html", html)}
                              placeholder="Corpo do email. Use as variáveis do glossário abaixo."
                            />
                          </div>
                        )}

                        {/* Push template */}
                        {n.available_channels.includes("push") && (
                          <div>
                            <label className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <Smartphone size={12} /> Template Push
                            </label>
                            <input
                              type="text" placeholder="Título da push notification"
                              value={getVal(n, "push_title") || ""}
                              onChange={e => setField(n.notification_type, "push_title", e.target.value)}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white mb-2"
                            />
                            <textarea
                              placeholder="Corpo da push notification"
                              value={getVal(n, "push_body") || ""}
                              onChange={e => setField(n.notification_type, "push_body", e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white font-mono resize-y"
                            />
                          </div>
                        )}

                        {/* Variables glossary */}
                        <div>
                          <label className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2 block">
                            Glossário de Variáveis <span className="font-normal normal-case">(clique para inserir)</span>
                          </label>
                          <div className="relative mb-2">
                            <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400" />
                            <input
                              type="text" placeholder="Buscar variável..."
                              value={varSearch} onChange={e => setVarSearch(e.target.value)}
                              className="w-full pl-8 pr-8 py-2 text-xs rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                            />
                            {varSearch && <button onClick={() => setVarSearch("")} className="absolute right-2.5 top-2.5 text-zinc-400 hover:text-zinc-600"><X size={14} /></button>}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto pr-1">
                            {filteredVars.map(v => (
                              <button
                                key={v.key}
                                onClick={() => insertVariable(v.key)}
                                className="flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 group transition-colors"
                              >
                                <code className="text-[11px] font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded flex-shrink-0 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50">
                                  {v.key}
                                </code>
                                <span className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">{v.description}</span>
                                {copied === v.key && <Check size={12} className="text-green-500 flex-shrink-0" />}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                          <Button
                            onClick={() => handleSave(n.notification_type)}
                            disabled={!hasDraft || updateMut.isPending}
                            className="text-xs"
                          >
                            <Save size={14} className="mr-1.5" />
                            {updateMut.isPending ? "Salvando..." : "Salvar"}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => handleReset(n.notification_type)}
                            disabled={resetMut.isPending}
                            className="text-xs text-zinc-500"
                          >
                            <RotateCcw size={14} className="mr-1.5" />
                            Restaurar Padrão
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
